import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getActiveCompanyId } from '@/shared/activeCompany';
import MaestroReply from '@/shared/MaestroReply';
import { 
  Card, 
  Input, 
  Button, 
  List, 
  Avatar, 
  Typography, 
  Space, 
  Tag, 
  Progress, 
  Divider,
  Tooltip,
  Badge,
  message
} from 'antd';
import { 
  SendOutlined, 
  RobotOutlined, 
  UserOutlined, 
  BulbOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  SettingOutlined,
  ApiOutlined,
  DatabaseOutlined,
  CopyOutlined,
  RedoOutlined,
  AudioOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useAuthStore } from '@/store/auth';
import { useConnectorsStore } from '@/store/connectors';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AnalyticsChart } from './AnalyticsChart';
import MaestroWelcome from './MaestroWelcome';
import { getMaestroWelcomePrompts } from './maestroWelcomePrompts';
import './AIAssistantPanel.less';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// In production/staging, use window.location.origin; in dev, use env var or localhost
const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment 
  ? (import.meta.env.VITE_WEBSITE_API_BASE_URL || 'http://localhost:3001') 
  : window.location.origin;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const normalizeIdentityValue = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value._id || value.id || value.companyId || value.userId || value.toString?.() || '';
  }
  return '';
};

const getStoredAssistantUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const resolveAssistantIdentity = (user: any) => {
  const storedUser = getStoredAssistantUser() as Record<string, any>;
  const userRecord = (user || storedUser) as Record<string, any>;
  const userId = normalizeIdentityValue(
    userRecord?._id ||
    userRecord?.id ||
    storedUser?._id ||
    storedUser?.id ||
    localStorage.getItem('userId')
  );
  const companyId = normalizeIdentityValue(getActiveCompanyId(userRecord || storedUser));
  const companyName =
    userRecord?.companyName ||
    userRecord?.workspaceName ||
    storedUser?.companyName ||
    storedUser?.workspaceName ||
    localStorage.getItem('companyName') ||
    '';

  return { userId, companyId, companyName };
};

interface WorkflowPreview {
  name: string;
  description: string;
  source: string;
  target: string;
  actions: string[];
  steps: string[];
  status: 'ready' | 'pending' | 'error';
}

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    suggestions?: string[];
    workflowPreview?: WorkflowPreview;
    workflow?: any;
    intent?: any;
    analytics?: any;
    chartData?: any;
    responseType?: string;
  };
}

interface PageDataContext {
  // Analytics & Statistics
  statistics?: {
    transactionsPerHour?: number;
    errorRate?: number;
    averageLatency?: number;
    totalTransactions?: number;
    connectorName?: string;
  };
  // Transaction Data
  transactions?: Array<{
    transactionId: string;
    status: string;
    startDate: string;
    endDate: string;
    latency?: number;
    errorMessage?: string;
    payload?: any;
  }>;
  // Integration Configuration
  integrationConfig?: {
    source?: {
      name?: string;
      url?: string;
      type?: string;
      method?: string;
    };
    destination?: {
      name?: string;
      url?: string;
      type?: string;
      method?: string;
    };
    status?: string;
    lastRunDate?: string;
  };
  // Chart/Analytics Data
  analyticsData?: any;
}

interface AIAssistantPanelProps {
  visible: boolean;
  onClose: () => void;
  context?: 'integration-builder' | 'dashboard' | 'general';
  integrationId?: string;
  integrationName?: string;
  pageData?: PageDataContext; // NEW: Page data context
}

interface ChatReadinessState {
  loading: boolean;
  isReady: boolean;
  checks: {
    token: boolean;
    userId: boolean;
    companyId: boolean;
    aiService: boolean;
  };
}

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({ 
  visible, 
  onClose, 
  context = 'general',
  integrationId: propIntegrationId,
  integrationName: propIntegrationName,
  pageData
}) => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const connectors = useConnectorsStore((state) => state.connectors);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const [chatReadiness, setChatReadiness] = useState<ChatReadinessState>({
    loading: true,
    isReady: false,
    checks: {
      token: false,
      userId: false,
      companyId: false,
      aiService: false,
    }
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Detect page context from URL and extract integration ID
  const pageContext = useMemo(() => {
    const path = location.pathname;
    
    if (path.includes('/studio')) {
      const integrationId = propIntegrationId || path.split('/details/')[1]?.split('/')[0];
      return {
        type: 'integration-studio',
        title: 'Integration Studio',
        description: 'building your integration workflow',
        integrationId,
        integrationName: propIntegrationName
      };
    }
    
    if (path.includes('/data-mapping')) {
      const integrationId = propIntegrationId || path.split('/data-mapping/')[1]?.split('/')[0];
      return {
        type: 'data-mapping',
        title: 'Data Mapping',
        description: 'configuring field mappings',
        integrationId,
        integrationName: propIntegrationName
      };
    }
    
    if (path.startsWith('/details/')) {
      const integrationId = propIntegrationId || path.split('/details/')[1]?.split('/')[0];
      return {
        type: 'integration-details',
        title: 'Integration Details',
        description: 'reviewing integration analytics',
        integrationId,
        integrationName: propIntegrationName
      };
    }
    
    if (path.startsWith('/dashboard') || path === '/') {
      return {
        type: 'dashboard',
        title: 'Dashboard',
        description: 'viewing your integration overview',
        integrationName: propIntegrationName
      };
    }

    if (path.includes('/intelligence-insights') || path.includes('/intelligence-studio')) {
      return {
        type: 'dashboard',
        title: 'Intelligence',
        description: 'reviewing operational insights',
        integrationName: propIntegrationName
      };
    }
    
    if (path.includes('/integration')) {
      const integrationId = propIntegrationId || path.split('/integration/')[1]?.split('/')[0];
      return {
        type: 'integration-builder',
        title: 'Integration Builder',
        description: 'creating a new integration',
        integrationId,
        integrationName: propIntegrationName
      };
    }
    
    return {
      type: 'general',
      title: 'Datacord',
      description: 'exploring the platform',
      integrationName: propIntegrationName
    };
  }, [location.pathname, propIntegrationId, propIntegrationName]);

  const welcomePrompts = useMemo(
    () => getMaestroWelcomePrompts(connectors, pageContext.type, pageContext.integrationName),
    [connectors, pageContext.type, pageContext.integrationName]
  );

  const isWelcome = messages.length === 0;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (visible) {
      checkChatReadiness();
    }
  }, [visible, user?._id, user?.primaryCompanyId]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, onClose]);

  const checkChatReadiness = async () => {
    const token = localStorage.getItem('accessToken');
    const { userId, companyId } = resolveAssistantIdentity(user);
    const baseChecks = {
      token: !!token,
      userId: !!userId,
      companyId: !!companyId,
      aiService: false,
    };

    setChatReadiness({
      loading: true,
      isReady: false,
      checks: baseChecks,
    });

    if (!baseChecks.token || !baseChecks.userId || !baseChecks.companyId) {
      setChatReadiness({
        loading: false,
        isReady: false,
        checks: baseChecks,
      });
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ai/health`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 5000
      });

      const aiHealthy = response.data?.status === 'healthy' || response.data?.aiService?.status === 'healthy';
      const checks = {
        ...baseChecks,
        aiService: !!aiHealthy,
      };

      setChatReadiness({
        loading: false,
        isReady: Object.values(checks).every(Boolean),
        checks,
      });
    } catch (error) {
      setChatReadiness({
        loading: false,
        isReady: false,
        checks: {
          ...baseChecks,
          aiService: false,
        }
      });
    }
  };

  const readinessSummary = [
    { label: 'Auth', ok: chatReadiness.checks.token },
    { label: 'User', ok: chatReadiness.checks.userId },
    { label: 'Customer', ok: chatReadiness.checks.companyId },
    { label: 'AI Service', ok: chatReadiness.checks.aiService },
  ];

  const readinessColor = chatReadiness.loading
    ? '#1677ff'
    : chatReadiness.isReady
      ? '#52c41a'
      : '#ff4d4f';

  const readinessLabel = chatReadiness.loading
    ? 'Checking chat readiness'
    : chatReadiness.isReady
      ? 'Chat ready'
      : 'Chat not ready';

  const readinessTooltip = `${readinessLabel}: ${readinessSummary
    .map((item) => `${item.label} ${item.ok ? 'OK' : 'Missing'}`)
    .join(' | ')}`;

  const shouldUseContextualFallback = ['integration-details', 'integration-studio', 'data-mapping', 'integration-builder'].includes(pageContext.type);

  const buildUnavailableAssistantMessage = (content: string): Message => ({
    id: Date.now().toString(),
    type: 'assistant',
    content,
    timestamp: new Date(),
    metadata: {
      confidence: 0.25,
      suggestions: ['Retry question']
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const playSpeech = async (text: string) => {
    const token = localStorage.getItem('accessToken');
    const { userId, companyId } = resolveAssistantIdentity(user);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/ai/voice/speak`,
        { text, companyId, userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const b64 = res.data?.data?.audioBase64;
      if (b64) {
        const audio = new Audio(`data:${res.data?.data?.mimeType || 'audio/mpeg'};base64,${b64}`);
        await audio.play();
        return;
      }
    } catch {
      /* fall through to browser TTS */
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      const utter = new SpeechSynthesisUtterance(text.slice(0, 4000));
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    }
  };

  const toggleVoice = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      message.error('Voice is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 1000) {
          message.warning('Recording was too short. Click the mic, speak, then click again to send.');
          return;
        }
        setTranscribing(true);
        try {
          const token = localStorage.getItem('accessToken');
          const { userId, companyId } = resolveAssistantIdentity(user);
          const audioBase64 = await blobToBase64(blob);
          const res = await axios.post(
            `${API_BASE_URL}/api/ai/voice/transcribe`,
            { audioBase64, mimeType: rec.mimeType || 'audio/webm', companyId, userId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const spoken = res.data?.data?.text?.trim();
          if (!spoken) {
            message.warning('Could not hear that. Try again.');
            return;
          }
          await handleSendMessage(spoken, true);
        } catch (err: any) {
          message.error(err?.response?.data?.message || 'Voice transcription failed.');
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start(250);
      setRecording(true);
    } catch {
      message.error('Microphone permission is required for voice.');
    }
  };

  const handleSendMessage = async (rawText?: string, fromVoice = false) => {
    const text = (typeof rawText === 'string' ? rawText : inputValue).trim();
    if (!text || isProcessing) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    // ✅ COMPREHENSIVE LOGGING
    console.log('🔵 USER MESSAGE:', {
      input: text,
      timestamp: new Date().toISOString(),
      context: context,
      userId: user?._id,
      conversationLength: messages.length
    });

    // Build conversation history for context
    const conversationHistory = messages.slice(-6).map(msg => ({
      role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
      content: msg.content,
      timestamp: msg.timestamp
    }));

    setInputValue('');
    setIsProcessing(true);

    // ALWAYS use Maestro with knowledge base for consistent experience
    try {
      console.log('🎭 Routing ALL questions to Maestro with Knowledge Base');
      
      // Get company ID from user or localStorage
      const { userId, companyId, companyName } = resolveAssistantIdentity(user);
      
      if (!companyId) {
        console.error('❌ No company ID found for Maestro query');
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          content: 'Sorry, I cannot process your request without a valid company context. Please ensure you are logged in properly.',
          type: 'assistant',
          timestamp: new Date()
        }]);
        setIsProcessing(false);
        return;
      }

      if (!userId) {
        console.error('❌ No user ID found for Maestro query');
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          content: 'Sorry, I cannot process your request without valid user credentials. Please log in again.',
          type: 'assistant',
          timestamp: new Date()
        }]);
        setIsProcessing(false);
        return;
      }
      
      const maestroResponse = await axios.post(
        `${API_BASE_URL}/api/ai/maestro/query`,
        {
          query: text,
          userId: userId,
          companyId: companyId,
          integrationId: pageContext.integrationId,
          context: {
            currentPage: location.pathname.includes('/workspace-lenses') ? 'workspace-lenses' : context,
            integrationContext: pageContext,
            conversationHistory: conversationHistory,
            customerName: companyName || undefined
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          },
          timeout: 30000
        }
      );
      
      let response;
      if (maestroResponse.data.success) {
        const maestroData = maestroResponse.data.data || {};
        const actionSuggestions = [
          ...(Array.isArray(maestroData.recommendedActions)
            ? maestroData.recommendedActions.map((a: any) => a?.action).filter(Boolean)
            : []),
          ...(Array.isArray(maestroData.followUpQuestions) ? maestroData.followUpQuestions : []),
        ].filter((q, i, all) => q && all.indexOf(q) === i).slice(0, 4);
        response = {
          success: true,
          data: {
            message: maestroData.synthesizedResponse || maestroData.response,
            type: maestroData.selfDiagnosis ? 'maestro-self-diagnosis' : 'maestro-knowledge',
            confidence: maestroData.confidence,
            agentResponses: maestroData.agentResponses,
            suggestions: actionSuggestions,
          }
        };
      } else {
        throw new Error(maestroResponse.data.message || 'Maestro query failed');
      }
      
      if (response.success && response.data?.message) {
        // ✅ COMPREHENSIVE AI RESPONSE LOGGING
        console.log('🟢 AI SERVICE RESPONSE:', {
          message: response.data.message,
          hasWorkflow: !!response.data.workflow,
          hasIntent: !!response.data.intent,
          hasAnalytics: !!response.data.analytics,
          responseType: response.data.type,
          suggestions: response.data.suggestions,
          confidence: response.data.intent?.confidence || 'N/A',
          timestamp: new Date().toISOString()
        });

        const aiResponse: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: response.data.message,
          timestamp: new Date(),
          metadata: {
            confidence: typeof response.data.confidence === 'number'
              ? response.data.confidence
              : (response.data.workflow
                ? (response.data.intent?.confidence || 0.9)
                : (response.data.intent?.confidence || 0.5)),
            suggestions: Array.isArray(response.data.suggestions) ? response.data.suggestions : [],
            responseType: response.data.type,
            analytics: response.data.analytics,
            chartData: response.data.chartData,
            workflowPreview: response.data.workflow ? {
              name: response.data.workflow.connector.integrationName,
              description: `${response.data.intent?.entities?.sourceSystem || 'Source'} to ${response.data.intent?.entities?.targetSystem || 'Target'} Integration`,
              source: response.data.intent?.entities?.sourceSystem || 'Source',
              target: response.data.intent?.entities?.targetSystem || 'Target',
              actions: [
                'Authentication configured',
                `${response.data.workflow.config.dataMapping?.length || 0} field mappings`,
                'Error handling included',
                'Production deployment ready'
              ],
              steps: [
                'Authentication configured',
                `${response.data.workflow.config.dataMapping?.length || 0} field mappings`,
                'Error handling included',
                'Production deployment ready'
              ],
              status: 'ready' as const
            } : undefined,
            workflow: response.data.workflow,
            intent: response.data.intent
          }
        };
        setMessages(prev => [...prev, aiResponse]);
        if (fromVoice && response.data.message) {
          void playSpeech(response.data.message);
        }
      } else {
        // AI service returned unsuccessful response, use intelligent fallback
        console.log('🟡 AI SERVICE UNSUCCESSFUL, using intelligent fallback:', response.message);
        if (shouldUseContextualFallback) {
          const fallbackResponse = generateAIResponse(text, context);
          setMessages(prev => [...prev, fallbackResponse]);
        } else {
          setMessages(prev => [...prev, buildUnavailableAssistantMessage(
            'I could not get a live Maestro answer for that question just now, so I do not want to guess. Please retry in a moment.'
          )]);
        }
      }
    } catch (error: any) {
      console.log('🔴 AI SERVICE ERROR, using intelligent fallback:', error);
      
      // Sanitize error for user display (never show internal URLs or technical details)
      let userMessage = 'I apologize, but I\'m having trouble processing your request right now.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('timed out')) {
        userMessage = 'The request is taking longer than expected. Let me try to help you with what I know.';
      } else if (error.response?.status === 503 || error.code === 'ECONNREFUSED') {
        userMessage = 'The AI service is temporarily unavailable. I\'ll use my local knowledge to assist you.';
      } else if (error.response?.status === 401) {
        userMessage = 'Your session may have expired. Please refresh the page if this continues.';
      }
      
      // Use intelligent context-aware fallback without showing technical error to user
      if (shouldUseContextualFallback) {
        const fallbackResponse = generateAIResponse(text, context);
        setMessages(prev => [...prev, fallbackResponse]);
      } else {
        setMessages(prev => [...prev, buildUnavailableAssistantMessage(
          `${userMessage} I could not reach live Maestro for that question, so I have not replaced it with a generic dashboard answer.`
        )]);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const generateAIResponse = (input: string, contextType: string): Message => {
    const lowerInput = input.toLowerCase();
    const integrationName = pageContext.integrationName || 'this integration';
    const integrationId = pageContext.integrationId;
    
    // Extract real data from pageData
    const stats = pageData?.statistics;
    const transactions = pageData?.transactions || [];
    const config = pageData?.integrationConfig;
    
    // Calculate insights from real data
    const failedTransactions = transactions.filter(t => t.status?.toLowerCase().includes('fail') || t.status?.toLowerCase().includes('error'));
    const successfulTransactions = transactions.filter(t => t.status?.toLowerCase().includes('success'));
    const avgLatency = stats?.averageLatency || 0;
    const errorRate = stats?.errorRate || 0;
    const totalTxns = stats?.totalTransactions || transactions.length;
    
    // Context-aware response generation based on page and input
    let content = '';
    let suggestions: string[] = [];
    let confidence = 0.85;

    // Integration Details page - analyze current data
    if (pageContext.type === 'integration-details') {
      if (lowerInput.includes('analyze') || lowerInput.includes('performance') || lowerInput.includes('data')) {
        const statusEmoji = errorRate > 5 ? '⚠️' : errorRate > 1 ? '⚡' : '✅';
        const latencyStatus = avgLatency > 2000 ? '🔴 High' : avgLatency > 1000 ? '🟡 Moderate' : '🟢 Good';
        
        content = `## 📊 Real-Time Performance Analysis\n\n` +
          `I'm analyzing **${integrationName}** using the live data on this page:\n\n` +
          `### Current Status ${statusEmoji}\n` +
          `• **Total Transactions**: ${totalTxns.toLocaleString()} processed\n` +
          `• **Success Rate**: ${(100 - errorRate).toFixed(2)}% (${successfulTransactions.length} successful)\n` +
          `• **Error Rate**: ${errorRate.toFixed(2)}% (${failedTransactions.length} failed)\n` +
          `• **Average Latency**: ${avgLatency}ms (${latencyStatus})\n` +
          `• **Throughput**: ${(stats?.transactionsPerHour || 0).toFixed(1)} transactions/hour\n\n` +
          `### ${errorRate > 5 ? '🚨 Critical' : errorRate > 1 ? '⚠️ Warning' : '✅ Healthy'} Health Assessment\n` +
          (errorRate > 5 ? 
            `Your error rate of ${errorRate.toFixed(2)}% is **critically high**. I found ${failedTransactions.length} failed transactions.\n\n` +
            `**Immediate Actions Needed:**\n` +
            `1. Review the failed transactions in the table below\n` +
            `2. Check authentication credentials\n` +
            `3. Verify endpoint URLs are correct\n` :
          errorRate > 1 ?
            `Your error rate of ${errorRate.toFixed(2)}% is above optimal. ${failedTransactions.length} transactions need attention.\n\n` :
            `Your integration is performing well! Only ${errorRate.toFixed(2)}% error rate.\n\n`) +
          (avgLatency > 2000 ?
            `**Latency Alert:** Your average response time of ${avgLatency}ms is high. Consider:\n` +
            `• Optimizing API queries\n` +
            `• Increasing timeout settings\n` +
            `• Reducing data payload size\n\n` : '') +
          `**Source**: ${config?.source?.name || 'Not configured'} → **Destination**: ${config?.destination?.name || 'Not configured'}`;
        
        suggestions = failedTransactions.length > 0 ? 
          ['Show me the failed transactions', 'How do I fix these errors?', 'Analyze error patterns'] :
          ['How can I optimize further?', 'Show me transaction details', 'Explain the metrics'];
      } else if (lowerInput.includes('error') || lowerInput.includes('fail') || lowerInput.includes('issue')) {
        content = `## 🔍 Error Analysis for ${integrationName}\n\n` +
          `To investigate errors for this integration:\n\n` +
          `### Step 1: Check Error Rate\n` +
          `Look at the **"Error rate %"** card at the top of this page. If it's above 0%, you have errors.\n\n` +
          `### Step 2: Review Exception Types\n` +
          `Scroll to the **"Exceptions by Type"** chart on this page:\n` +
          `• Red bars = 4xx errors (client/configuration issues)\n` +
          `• Orange bars = 5xx errors (server/destination issues)\n` +
          `• Click on a bar to filter by error type\n\n` +
          `### Step 3: View Transaction Details\n` +
          `Scroll to the **"Transactions"** table at the bottom:\n` +
          `• Look for rows with ❌ Failed status\n` +
          `• Click "View Details" to see the full error message and payload\n\n` +
          `### Common Error Patterns:\n` +
          `• **401/403 errors**: Authentication issue - verify credentials in Integration Studio\n` +
          `• **404 errors**: Endpoint URL is incorrect\n` +
          `• **500 errors**: Destination API is down or has issues\n` +
          `• **Timeout errors**: Increase timeout settings or optimize queries`;
        suggestions = ['How do I fix authentication errors?', 'View failed transactions', 'Test my connection'];
      } else if (lowerInput.includes('optimize') || lowerInput.includes('improve') || lowerInput.includes('faster')) {
        content = `## ⚡ Optimization Tips for ${integrationName}\n\n` +
          `Here's how to optimize this integration:\n\n` +
          `### 🎯 Performance Optimizations:\n` +
          `1. **Adjust Polling Interval**\n` +
          `   • Go to Integration Studio (button above)\n` +
          `   • Look at "Endpoint Poll Interval"\n` +
          `   • If transaction volume is low, increase interval to reduce unnecessary API calls\n\n` +
          `2. **Optimize Data Mapping**\n` +
          `   • Click "Configure Mapping" → "Data Mapping"\n` +
          `   • Remove unnecessary field mappings\n` +
          `   • Simplify transformation logic\n\n` +
          `3. **Batch Processing**\n` +
          `   • Check if your destination API supports batch operations\n` +
          `   • Process multiple records in one API call\n\n` +
          `### 📊 Monitor These Metrics:\n` +
          `• **Avg Requests per hour**: Should match your business needs\n` +
          `• **Avg Latency**: Aim for <1000ms\n` +
          `• **Error rate**: Keep below 1%\n\n` +
          `*Currently viewing: ${integrationId?.substring(0, 12)}...*`;
        suggestions = ['Change polling interval', 'Simplify data mapping', 'Enable batch processing'];
      } else if (lowerInput.includes('metric') || lowerInput.includes('chart') || lowerInput.includes('mean')) {
        content = `## 📈 Understanding Your Metrics\n\n` +
          `Let me explain the key metrics visible on this page:\n\n` +
          `### Top Cards (Analytic Summary):\n` +
          `1. **Avg Requests per hour**: Total transactions divided by hours in selected period\n` +
          `   • Shows how busy your integration is\n\n` +
          `2. **Error rate %**: (Failed transactions / Total transactions) × 100\n` +
          `   • Healthy integrations typically have <1% error rate\n\n` +
          `3. **Avg Latency**: Average time from request to response\n` +
          `   • Includes: API call time + mapping time + destination write time\n\n` +
          `4. **Total Transactions**: Sum of all processed records in the selected time period\n\n` +
          `### Charts:\n` +
          `• **Volume Over Time**: Transaction count per time period (click bars to drill down)\n` +
          `• **Exceptions by Type**: Errors grouped by HTTP status code\n` +
          `• **Processing Latency**: Time breakdown showing where delays occur\n` +
          `• **Size Over Time**: Data volume (bytes) processed over time\n\n` +
          `*Tip: Use the date filter at the top to adjust your analysis period.*`;
        suggestions = ['What is a good error rate?', 'Why is my latency high?', 'How do I export this data?'];
      } else {
        content = `I'm here to help with **${integrationName}**! The performance data is visible on this page. What would you like to know?\n\n` +
          `I can help you:\n` +
          `• Analyze the charts and metrics shown above\n` +
          `• Explain what the numbers mean for your integration\n` +
          `• Identify issues from the visible error patterns\n` +
          `• Suggest optimizations based on the current data\n\n` +
          `Just ask me about anything you see on this page!`;
        suggestions = ['Analyze the performance data', 'Explain these metrics', 'Show me error patterns', 'Optimize this integration'];
      }
    }
    // Integration Studio page
    else if (pageContext.type === 'integration-studio') {
      if (lowerInput.includes('configure') || lowerInput.includes('setup') || lowerInput.includes('auth')) {
        content = `## ⚙️ Configuring ${integrationName}\n\n` +
          `I can see you're in the Integration Studio. Here's how to configure this integration:\n\n` +
          `### Source Configuration:\n` +
          `1. **URL**: Enter your source API endpoint\n` +
          `2. **Request Method**: Usually GET for reading data\n` +
          `3. **Authentication**: Click the "Authorization" tab\n` +
          `   • Choose authentication type (API Key, OAuth, Basic Auth)\n` +
          `   • Enter credentials\n` +
          `4. **Headers**: Add any required headers (e.g., Content-Type)\n` +
          `5. **Polling Interval**: How often to check for new data (in seconds)\n\n` +
          `### Destination Configuration:\n` +
          `1. **URL**: Enter your destination API endpoint\n` +
          `2. **Request Method**: Usually POST or PUT for writing data\n` +
          `3. **Authentication**: Same process as source\n\n` +
          `*After configuration, click "Test Connection" to verify it works.*`;
        suggestions = ['How do I add API key authentication?', 'Test my connection', 'What polling interval should I use?'];
      } else {
        content = `You're configuring **${integrationName}** in the Integration Studio. I can help you set up source, destination, authentication, and test your connections.\n\nWhat do you need help with?`;
        suggestions = ['Configure authentication', 'Test connection', 'Set polling interval', 'Add headers'];
      }
    }
    // Data Mapping page
    else if (pageContext.type === 'data-mapping') {
      if (lowerInput.includes('map') || lowerInput.includes('field') || lowerInput.includes('transform')) {
        content = `## 🗺️ Data Mapping for ${integrationName}\n\n` +
          `Field mapping connects your source data to destination fields:\n\n` +
          `### How to Map Fields:\n` +
          `1. **Source Field** (left): The field name from your source API\n` +
          `2. **Destination Field** (right): Where the data should go\n` +
          `3. **Transformation** (optional): Convert the data format\n\n` +
          `### Common Transformations:\n` +
          `• **String**: Convert to text\n` +
          `• **Number**: Convert to numeric value\n` +
          `• **Date**: Format date/time\n` +
          `• **Boolean**: Convert to true/false\n` +
          `• **Custom**: Write JavaScript transformation\n\n` +
          `### Tips:\n` +
          `• Map only the fields you actually need\n` +
          `• Use consistent naming conventions\n` +
          `• Test with sample data before deploying`;
        suggestions = ['Add a calculated field', 'Transform date format', 'Validate my mapping'];
      } else {
        content = `You're setting up data mapping for **${integrationName}**. I can help you map fields, apply transformations, and validate your configuration.\n\nWhat mapping do you need help with?`;
        suggestions = ['How do I map nested fields?', 'Add a transformation', 'Test my mapping'];
      }
    }
    // Dashboard
    else if (pageContext.type === 'dashboard') {
      content = `I can help you with your integrations dashboard. What would you like to do?`;
      suggestions = ['Create a new integration', 'Which integrations need attention?', 'Show system health'];
    }
    // Default fallback
    else {
      content = `I'm here to help! Based on your question "${input}", I can assist you with integration setup, configuration, troubleshooting, and optimization.\n\nWhat would you like to know?`;
      suggestions = ['Create new integration', 'Troubleshoot errors', 'Optimize performance'];
    }
    
    return {
      id: Date.now().toString(),
      type: 'assistant',
      content,
      timestamp: new Date(),
      metadata: {
        confidence,
        suggestions
      }
    };
  };

  const handleSuggestionClick = (suggestion: string) => {
    console.log('🔵 SUGGESTION CLICKED:', suggestion);

    if (/settings\s*→\s*ai integrations|open settings → ai/i.test(suggestion)) {
      window.location.href = '/settings?tab=ai';
      return;
    }
    if (/agents\s*→\s*vault|open agents → vault/i.test(suggestion)) {
      window.location.href = '/settings?tab=agents';
      return;
    }
    if (/try the question again/i.test(suggestion)) {
      const lastUser = [...messages].reverse().find((m) => m.type === 'user');
      void handleSendMessage(lastUser?.content || suggestion);
      return;
    }
    
    // Handle special credential actions
    if (suggestion.includes('Add') && suggestion.includes('credentials')) {
      handleCredentialAction(suggestion);
      return;
    }
    
    // Handle field mapping actions
    if (suggestion.includes('Configure field mappings') || suggestion.includes('Review suggested mappings')) {
      handleFieldMappingAction(suggestion);
      return;
    }

    void handleSendMessage(suggestion);
  };

  const handleCredentialAction = (action: string) => {
    const credentialMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: `🔐 **Credential Collection**\n\nTo proceed with your integration, I need to collect your API credentials securely. Please provide:\n\n**For ${action.includes('Salesforce') ? 'Salesforce' : 'HubSpot'}:**\n- API Key\n- Instance URL ${action.includes('Salesforce') ? '(if applicable)' : ''}\n\nOnce you provide these credentials, I'll:\n1. ✅ Create the integration in your dashboard\n2. 🔗 Test the connection\n3. 📋 Show you the configurable field mappings\n4. 🚀 Prepare it for deployment\n\nPlease enter your credentials in the chat below:`,
      timestamp: new Date(),
      metadata: {
        confidence: 1.0,
        suggestions: ['I have the credentials ready', 'Show me where to find API keys', 'Skip for now']
      }
    };
    setMessages(prev => [...prev, credentialMessage]);
  };

  const handleFieldMappingAction = (action: string) => {
    const mappingMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: `🔄 **Field Mapping Configuration**\n\nI'll help you configure the data mappings between your systems. Let me first create the integration and then show you the mapping interface.\n\n**Next Steps:**\n1. 🏗️ Creating integration in your dashboard...\n2. 📋 Opening field mapping interface\n3. 🎯 Showing suggested mappings\n\nThis will take you to the integration builder where you can configure how data flows between your systems.`,
      timestamp: new Date(),
      metadata: {
        confidence: 1.0,
        suggestions: ['Create integration now', 'Show me mapping examples', 'Go to dashboard']
      }
    };
    setMessages(prev => [...prev, mappingMessage]);
    
    // Simulate creating integration and navigating
    setTimeout(() => {
      const successMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `✅ **Integration Created!**\n\nI've created your Salesforce to HubSpot integration. You can now:\n\n🎯 **Configure Field Mappings** - Set up how data flows between systems\n🔐 **Add Authentication** - Connect your accounts securely\n🧪 **Test Integration** - Run a test to verify everything works\n🚀 **Deploy** - Make it live when ready\n\nWould you like me to take you to the integration builder now?`,
        timestamp: new Date(),
        metadata: {
          confidence: 1.0,
          suggestions: ['Open integration builder', 'Configure mappings', 'Add authentication']
        }
      };
      setMessages(prev => [...prev, successMessage]);
    }, 2000);
  };

  const handleCreateIntegration = async (workflow: any, intent: any) => {
    try {
      setIsProcessing(true);
      
      // Add a message showing the integration is being created
      const creatingMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: '🚀 **Creating your integration...** \n\nI\'m setting up your Salesforce to HubSpot integration in the Datacord platform. This will take a moment.',
        timestamp: new Date(),
        metadata: { confidence: 1.0 }
      };
      setMessages(prev => [...prev, creatingMessage]);

      // Here you would typically save the workflow to your backend
      // For now, let's simulate the process and redirect to the integration builder
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      const successMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '✅ **Integration Created Successfully!** \n\nYour Salesforce to HubSpot integration has been created and saved to your dashboard. You can now configure the authentication and deploy it.',
        timestamp: new Date(),
        metadata: { 
          confidence: 1.0,
          suggestions: ['Configure Authentication', 'View Integration', 'Deploy to Staging']
        }
      };
      setMessages(prev => [...prev, successMessage]);

      // Optional: Navigate to the integration builder or dashboard
      // navigate('/integrations/new', { state: { workflow, intent } });
      
    } catch (error) {
      console.error('Error creating integration:', error);
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: '❌ **Integration Creation Failed** \n\nThere was an error creating your integration. Please try again or contact support.',
        timestamp: new Date(),
        metadata: { confidence: 1.0 }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Copy message content to clipboard
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      message.success('Copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy');
    });
  };

  // Retry a question
  const handleRetry = (questionText: string) => {
    void handleSendMessage(questionText);
  };

  const renderMessage = (msg: Message) => {
    const isUser = msg.type === 'user';
    
    return (
      <div key={msg.id} className={`message ${isUser ? 'user-message' : 'ai-message'}`}>
        <div className="message-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            {isUser ? (
              <Avatar 
                icon={<UserOutlined />} 
                className="user-avatar"
                size="small"
              />
            ) : (
              <div className="modern-ai-avatar">
                <div className="ai-avatar-gradient">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#254B70" />
                        <stop offset="50%" stopColor="#1664FF" />
                        <stop offset="100%" stopColor="#4FB3FF" />
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="3" fill="url(#aiGradient)" opacity="0.8">
                      <animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="12" cy="12" r="6" fill="none" stroke="url(#aiGradient)" strokeWidth="1" opacity="0.6">
                      <animate attributeName="r" values="6;8;6" dur="3s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.6;0.2;0.6" dur="3s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="12" cy="12" r="9" fill="none" stroke="url(#aiGradient)" strokeWidth="0.5" opacity="0.3">
                      <animate attributeName="r" values="9;11;9" dur="4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.3;0.1;0.3" dur="4s" repeatCount="indefinite" />
                    </circle>
                    <g transform="translate(12,12)">
                      <path d="M-2,-2 L2,-2 L2,2 L-2,2 Z" fill="white" opacity="0.9" rx="0.5">
                        <animateTransform attributeName="transform" type="rotate" values="0;360" dur="8s" repeatCount="indefinite" />
                      </path>
                      <circle cx="0" cy="0" r="1" fill="url(#aiGradient)" opacity="0.7" />
                    </g>
                  </svg>
                </div>
              </div>
            )}
            <Text type="secondary" className="message-time">
              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </Space>
          <Space size="small">
            {isUser ? (
              <Tooltip title="Retry this question">
                <Button
                  type="text"
                  size="small"
                  icon={<RedoOutlined />}
                  onClick={() => handleRetry(msg.content)}
                  style={{ opacity: 0.6 }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Copy response">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(msg.content)}
                  style={{ opacity: 0.6 }}
                />
              </Tooltip>
            )}
          </Space>
        </div>
        
        <div className="message-content">
          {isUser ? (
            <Paragraph className="message-text">{msg.content}</Paragraph>
          ) : (
            <div className="ai-message-markdown">
              <MaestroReply content={msg.content} />
            </div>
          )}
          
          {/* Render Analytics Charts if available */}
          {!isUser && msg.metadata?.responseType === 'advanced_analytics' && msg.metadata?.analytics && (
            <AnalyticsChart data={msg.metadata.analytics} />
          )}
          
          {!isUser && msg.metadata?.responseType !== 'maestro-self-diagnosis' && msg.metadata?.confidence && (
            <div className="ai-metadata">
              <Space>
                <Tooltip title="AI Confidence Score">
                  <Progress 
                    percent={Math.round(msg.metadata.confidence * 100)} 
                    size="small" 
                    showInfo={false}
                    strokeColor="#1677ff"
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {Math.round(msg.metadata.confidence * 100)}% confident
                  </Text>
                </Tooltip>
              </Space>
            </div>
          )}
          
          {!isUser && msg.metadata?.suggestions && msg.metadata.suggestions.length > 0 && (
            <div className="suggestions">
              <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                <BulbOutlined /> Quick actions:
              </Text>
              <Space wrap>
                {msg.metadata?.suggestions?.map((suggestion, index) => (
                  <Tag 
                    key={index}
                    className="suggestion-tag"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </Tag>
                )) || []}
              </Space>
            </div>
          )}

          {/* Add credential collection buttons based on message content */}
          {!isUser && msg.content?.toLowerCase().includes('api key') && msg.content?.toLowerCase().includes('salesforce') && (
            <div className="credential-actions" style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '8px' }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>🔐 Secure Credential Entry:</Text>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={<SettingOutlined />}
                  onClick={() => handleSuggestionClick('Add Salesforce credentials')}
                  block
                  size="small"
                >
                  Add Salesforce Credentials
                </Button>
                <Button 
                  type="default" 
                  icon={<ApiOutlined />}
                  onClick={() => handleSuggestionClick('Add HubSpot credentials')}
                  block
                  size="small"
                >
                  Add HubSpot Credentials
                </Button>
              </Space>
              <Text type="secondary" style={{ fontSize: '11px', marginTop: '8px', display: 'block' }}>
                🔒 All credentials are encrypted and stored securely
              </Text>
            </div>
          )}

          {/* Field mapping actions */}
          {!isUser && msg.content?.toLowerCase().includes('field mapping') && (
            <div className="mapping-actions" style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>🔄 Data Mapping:</Text>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={<DatabaseOutlined />}
                  onClick={() => handleSuggestionClick('Configure field mappings')}
                  block
                  size="small"
                >
                  Configure Field Mappings
                </Button>
                <Button 
                  type="default" 
                  onClick={() => handleSuggestionClick('Review suggested mappings')}
                  block
                  size="small"
                >
                  Review AI Suggestions (7 mappings)
                </Button>
              </Space>
            </div>
          )}
          
          {!isUser && msg.metadata?.workflowPreview && (
            <Card size="small" className="workflow-preview">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>
                  <ThunderboltOutlined /> Suggested Workflow
                </Text>
                <Space>
                  <Tag color="blue">{msg.metadata?.workflowPreview?.source || 'Source'}</Tag>
                  <Text>→</Text>
                  <Tag color="green">{msg.metadata?.workflowPreview?.target || 'Target'}</Tag>
                </Space>
                <div>
                  {msg.metadata?.workflowPreview?.actions?.map((action: string, index: number) => (
                    <Tag key={index} icon={<CheckCircleOutlined />} color="success">
                      {action}
                    </Tag>
                  )) || []}
                </div>
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => handleCreateIntegration(msg.metadata?.workflow, msg.metadata?.intent)}
                >
                  Create This Integration
                </Button>
              </Space>
            </Card>
          )}
        </div>
      </div>
    );
  };

  if (!visible) return null;

  return (
    <>
      <button
        type="button"
        className="ai-assistant-backdrop"
        aria-label="Close Maestro"
        onClick={onClose}
      />
      <div
        className={`ai-assistant-panel ${isWelcome ? 'is-welcome' : 'is-chat'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Maestro"
      >
      <Card 
        title={
          <Space>
            <Tooltip title={readinessTooltip}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: readinessColor,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${readinessColor}22`,
                  cursor: 'help'
                }}
              />
            </Tooltip>
            <div className="ai-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="headerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#254B70" />
                    <stop offset="50%" stopColor="#1664FF" />
                    <stop offset="100%" stopColor="#4FB3FF" />
                  </linearGradient>
                </defs>
                <circle cx="12" cy="12" r="10" fill="url(#headerGradient)" opacity="0.1" />
                <circle cx="12" cy="12" r="6" fill="none" stroke="url(#headerGradient)" strokeWidth="1.5" opacity="0.8">
                  <animate attributeName="r" values="6;7;6" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0.4;0.8" dur="3s" repeatCount="indefinite" />
                </circle>
                <circle cx="12" cy="12" r="3" fill="url(#headerGradient)" opacity="0.9">
                  <animate attributeName="r" values="3;3.5;3" dur="2s" repeatCount="indefinite" />
                </circle>
                <g transform="translate(12,12)">
                  <rect x="-1.5" y="-1.5" width="3" height="3" fill="white" opacity="0.9" rx="0.5">
                    <animateTransform attributeName="transform" type="rotate" values="0;360" dur="6s" repeatCount="indefinite" />
                  </rect>
                  <circle cx="0" cy="0" r="0.8" fill="url(#headerGradient)" opacity="0.8" />
                </g>
                <circle cx="8" cy="8" r="1" fill="url(#headerGradient)" opacity="0.6">
                  <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="16" cy="16" r="1" fill="url(#headerGradient)" opacity="0.6">
                  <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2.5s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
            <Text strong style={{ fontSize: '15px' }}>Maestro</Text>
          </Space>
        }
        extra={
          <Space size={4}>
            {!isWelcome && (
              <Tooltip title="New conversation">
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setMessages([])}
                  aria-label="Start a new conversation"
                >
                  New
                </Button>
              </Tooltip>
            )}
            <Button type="text" onClick={onClose} aria-label="Close Maestro">
              ×
            </Button>
          </Space>
        }
        className="ai-panel-card"
      >
        <div className={`messages-container ${isWelcome ? 'is-welcome' : ''}`}>
          {isWelcome ? (
            <MaestroWelcome
              firstName={user?.firstName}
              email={user?.email}
              primary={welcomePrompts.primary}
              more={welcomePrompts.more}
              onSelect={(text) => void handleSendMessage(text)}
            />
          ) : (
            messages.map(renderMessage)
          )}
          {isProcessing && (
            <div className="message ai-message">
              <div className="message-header">
                <div className="modern-ai-avatar processing">
                  <div className="ai-avatar-gradient">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="aiGradientProcessing" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#254B70" />
                          <stop offset="50%" stopColor="#1664FF" />
                          <stop offset="100%" stopColor="#4FB3FF" />
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="3" fill="url(#aiGradientProcessing)" opacity="0.8">
                        <animate attributeName="r" values="2;5;2" dur="1s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.8;0.3;0.8" dur="1s" repeatCount="indefinite" />
                      </circle>
                      <circle cx="12" cy="12" r="6" fill="none" stroke="url(#aiGradientProcessing)" strokeWidth="2" opacity="0.6">
                        <animate attributeName="r" values="6;9;6" dur="1.5s" repeatCount="indefinite" />
                        <animate attributeName="strokeWidth" values="2;0.5;2" dur="1.5s" repeatCount="indefinite" />
                      </circle>
                      <g transform="translate(12,12)">
                        <circle cx="0" cy="0" r="1" fill="white" opacity="0.9">
                          <animate attributeName="r" values="0.5;1.5;0.5" dur="0.8s" repeatCount="indefinite" />
                        </circle>
                      </g>
                    </svg>
                  </div>
                </div>
                <Text type="secondary" className="message-time">Processing...</Text>
              </div>
              <div className="message-content">
                <Text type="secondary">Maestro is thinking...</Text>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <Divider style={{ margin: isWelcome ? 0 : '16px 0', display: isWelcome ? 'none' : undefined }} />
        
        <div className="input-area">
          <form 
            className="ai-assistant-input-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={chatReadiness.isReady ? "Ask, build, search, @ to give context" : "Waiting for chat readiness checks..."}
                autoSize={{ minRows: 1, maxRows: 3 }}
                disabled={isProcessing || recording || transcribing || chatReadiness.loading || !chatReadiness.isReady}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    void handleSendMessage();
                  }
                }}
              />
              <Button
                type={recording ? 'primary' : 'default'}
                danger={recording}
                icon={<AudioOutlined />}
                onClick={() => void toggleVoice()}
                disabled={isProcessing || transcribing || chatReadiness.loading || !chatReadiness.isReady}
                title={recording ? 'Stop recording' : transcribing ? 'Transcribing…' : 'Voice — click to speak, click again to send'}
                aria-label={recording ? 'Stop voice recording' : 'Start voice recording'}
                className="voice-mic-btn"
              />
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                onClick={() => void handleSendMessage()}
                loading={isProcessing}
                disabled={!inputValue.trim() || recording || transcribing || chatReadiness.loading || !chatReadiness.isReady}
              />
            </Space.Compact>
          </form>
          
          <Text type="secondary" style={{ fontSize: '11px', marginTop: '8px', display: 'block', textAlign: 'center' }}>
            Maestro is AI and can make mistakes.
          </Text>
        </div>
      </Card>
      </div>
    </>
  );
};

export default AIAssistantPanel;
